/**
 * GallerySection -- 갤러리 섹션 편집
 */

import { useCallback, useRef, useState } from 'react'
import { Upload, Sparkles, ImagePlus } from 'lucide-react'
import { useInvitationStore } from '../../../stores/invitationStore.js'
import { apiFetch } from '../../../lib/api.js'
import { Input, Button, Badge, Spinner } from '../../ui/index.js'
import { GalleryImageGrid } from './GalleryImageGrid.js'
import { AIPhotoImportModal } from './AIPhotoImportModal.js'
import styles from './SectionForm.module.css'

const MAX_GALLERY_IMAGES = 20
const MAX_UPLOAD_BATCH = 10

const GALLERY_LAYOUTS: { value: string; label: string }[] = [
  { value: 'grid-2', label: '2열' },
  { value: 'grid-3', label: '3열' },
  { value: 'grid-2-1', label: '2+1' },
  { value: 'single-column', label: '1열' },
  { value: 'masonry', label: '핀터레스트' },
  { value: 'carousel', label: '캐러셀' },
  { value: 'filmstrip', label: '필름' },
  { value: 'highlight', label: '하이라이트' },
]

const ASPECT_RATIOS: { value: string; label: string }[] = [
  { value: '1:1', label: '정사각' },
  { value: '4:3', label: '가로' },
  { value: '3:4', label: '세로' },
  { value: '16:9', label: '와이드' },
]

/** 비율 피커를 숨기는 레이아웃 (carousel, filmstrip, grid-2-1, highlight) */
const RATIO_EXCLUDED_LAYOUTS = new Set(['carousel', 'filmstrip', 'grid-2-1', 'highlight'])

export function GallerySection() {
  const invitation = useInvitationStore((s) => s.invitation)
  const updateNested = useInvitationStore((s) => s.updateNested)
  const [isUploading, setIsUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [showAIImport, setShowAIImport] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  if (!invitation) return null

  const currentLayout = invitation.settings?.galleryLayout ?? 'grid-2'
  const showRatioPicker = !RATIO_EXCLUDED_LAYOUTS.has(currentLayout)
  const images: string[] = invitation.gallery?.images ?? []
  const currentCount = images.length
  const canUpload = currentCount < MAX_GALLERY_IMAGES

  const handleUpload = useCallback(
    async (files: FileList | null) => {
      if (!files?.length || !invitation?.id) return
      setIsUploading(true)
      setUploadError(null)

      const formData = new FormData()
      const filesToUpload = Array.from(files).slice(0, MAX_UPLOAD_BATCH)
      filesToUpload.forEach((f) => formData.append('files', f))
      formData.append('invitationId', invitation.id)

      try {
        const res = await apiFetch<{
          urls: string[]
          count: number
          total: number
          limit: number
          errors?: string[]
        }>('/api/upload/gallery', {
          method: 'POST',
          body: formData,
        })
        if (res.success) {
          updateNested('gallery.images', [...images, ...res.data.urls])
          if (res.data.errors?.length) {
            setUploadError(`일부 파일 업로드 실패: ${res.data.errors.join(', ')}`)
          }
        } else {
          const errMsg = typeof res.error === 'string' ? res.error : res.error.message
          setUploadError(errMsg)
        }
      } catch {
        setUploadError('업로드 중 오류가 발생했습니다')
      } finally {
        setIsUploading(false)
        if (fileInputRef.current) fileInputRef.current.value = ''
      }
    },
    [invitation?.id, images, updateNested]
  )

  const handleDelete = useCallback(
    (index: number) => {
      const updated = images.filter((_, i) => i !== index)
      updateNested('gallery.images', updated)
    },
    [images, updateNested]
  )

  const handleReorder = useCallback(
    (reordered: string[]) => {
      updateNested('gallery.images', reordered)
    },
    [updateNested]
  )

  const handleAIImport = useCallback(
    (urls: string[]) => {
      updateNested('gallery.images', [...images, ...urls])
    },
    [images, updateNested]
  )

  return (
    <div className={styles.form}>
      <div className={styles.fieldGroup}>
        <span className={styles.subSectionTitle}>갤러리 레이아웃</span>
        <div className={styles.layoutPicker}>
          {GALLERY_LAYOUTS.map((layout) => (
            <button
              key={layout.value}
              type="button"
              className={`${styles.layoutCard} ${(invitation.settings?.galleryLayout ?? 'grid-2') === layout.value ? styles.layoutCardActive : ''}`}
              onClick={() => updateNested('settings.galleryLayout', layout.value)}
            >
              <LayoutPreview layout={layout.value} />
              <span className={styles.layoutLabel}>{layout.label}</span>
            </button>
          ))}
        </div>
      </div>

      {showRatioPicker && (
        <div className={styles.fieldGroup}>
          <span className={styles.subSectionTitle}>사진 비율</span>
          <div className={styles.layoutPicker}>
            {ASPECT_RATIOS.map((ratio) => (
              <button
                key={ratio.value}
                type="button"
                className={`${styles.layoutCard} ${(invitation.settings?.galleryAspectRatio ?? '1:1') === ratio.value ? styles.layoutCardActive : ''}`}
                onClick={() => updateNested('settings.galleryAspectRatio', ratio.value)}
              >
                <RatioPreview ratio={ratio.value} />
                <span className={styles.layoutLabel}>{ratio.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className={styles.fieldGroup}>
        <div className={styles.galleryHeader}>
          <span className={styles.subSectionTitle}>사진 관리</span>
          <Badge color="gray">
            {currentCount} / {MAX_GALLERY_IMAGES}장
          </Badge>
        </div>

        <GalleryImageGrid images={images} onReorder={handleReorder} onDelete={handleDelete} />

        {images.length === 0 && (
          <div className={styles.galleryEmpty}>
            <ImagePlus size={32} strokeWidth={1.2} />
            <p>아직 사진이 없습니다</p>
            <p className={styles.hint}>
              아래 버튼으로 사진을 업로드하거나 AI 사진을 가져오세요
            </p>
          </div>
        )}

        {uploadError && <p className={styles.galleryError}>{uploadError}</p>}

        <div className={styles.galleryActions}>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            multiple
            onChange={(e) => handleUpload(e.target.files)}
            style={{ display: 'none' }}
          />
          <Button
            variant="outline"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={!canUpload || isUploading}
          >
            {isUploading ? <Spinner size="sm" /> : <Upload size={16} />}
            {isUploading ? '업로드 중...' : '사진 업로드'}
          </Button>
          <Button variant="outline" size="sm" disabled={isUploading} onClick={() => setShowAIImport(true)}>
            <Sparkles size={16} />
            AI 사진 가져오기
          </Button>
        </div>

        {!canUpload && (
          <p className={styles.hint}>
            갤러리 한도에 도달했습니다 ({MAX_GALLERY_IMAGES}장)
          </p>
        )}
      </div>

      <Input
        label="커버 이미지 URL"
        value={invitation.gallery?.coverImage ?? ''}
        onChange={(e) => updateNested('gallery.coverImage', e.target.value)}
        placeholder="https://..."
      />

      <AIPhotoImportModal
        open={showAIImport}
        onClose={() => setShowAIImport(false)}
        existingImages={images}
        onImport={handleAIImport}
      />
    </div>
  )
}

/* ── LayoutPreview ── */

const PREVIEW_CONFIGS: Record<string, React.CSSProperties[][]> = {
  'grid-2': [
    [{ flex: 1, height: 17 }, { flex: 1, height: 17 }],
    [{ flex: 1, height: 17 }, { flex: 1, height: 17 }],
  ],
  'grid-3': [
    [{ flex: 1, height: 17 }, { flex: 1, height: 17 }, { flex: 1, height: 17 }],
    [{ flex: 1, height: 17 }, { flex: 1, height: 17 }, { flex: 1, height: 17 }],
  ],
  'grid-2-1': [
    [{ flex: 1, height: 20 }, { flex: 1, height: 20 }],
    [{ flex: 1, height: 14 }],
  ],
  'single-column': [
    [{ flex: 1, height: 10 }],
    [{ flex: 1, height: 10 }],
    [{ flex: 1, height: 10 }],
  ],
  masonry: [
    [{ flex: 1, height: 22 }, { flex: 1, height: 14 }],
    [{ flex: 1, height: 14 }, { flex: 1, height: 22 }],
  ],
  carousel: [
    [{ flex: 3, height: 30 }, { flex: 1, height: 30, opacity: 0.4 }],
  ],
  filmstrip: [
    [{ flex: 1, height: 26 }, { flex: 1, height: 26 }, { flex: 1, height: 26 }, { flex: 1, height: 26 }],
  ],
  highlight: [
    [{ flex: 1, height: 22 }],
    [{ flex: 1, height: 12 }, { flex: 1, height: 12 }],
  ],
}

/* ── RatioPreview ── */

const RATIO_SIZES: Record<string, { width: number; height: number }> = {
  '1:1': { width: 28, height: 28 },
  '4:3': { width: 32, height: 24 },
  '3:4': { width: 24, height: 32 },
  '16:9': { width: 36, height: 20 },
}

function RatioPreview({ ratio }: { ratio: string }) {
  const size = RATIO_SIZES[ratio] ?? RATIO_SIZES['1:1']
  return (
    <div className={styles.layoutPreview}>
      <div
        className={styles.layoutBlock}
        style={{ width: size.width, height: size.height }}
      />
    </div>
  )
}

function LayoutPreview({ layout }: { layout: string }) {
  const rows = PREVIEW_CONFIGS[layout]
  if (!rows) return null

  return (
    <div className={styles.layoutPreview}>
      {rows.map((cols, ri) => (
        <div key={ri} className={styles.layoutRow}>
          {cols.map((style, ci) => (
            <div key={ci} className={styles.layoutBlock} style={style} />
          ))}
        </div>
      ))}
    </div>
  )
}
